/**
 * The query params the main SIYA app appends when it hands a session to this origin.
 *
 * There are TWO names for the same JWT, and that is not a style choice:
 *
 *   - `authToken` — what every configured `urlPatterns.microapps` template emits
 *     (`https://microapps.{baseOnly}?node={domain}&authToken={userToken}`), and what
 *     login.html appends when it honours a returnUrl. This is the intended name.
 *
 *   - `userToken` — what the SIYA SPA's post-login redirect emits for users flagged
 *     `onlyMicroapps && !hasVmAccess`. That path does not go through the URL templates;
 *     it builds the URL inline and names the param after its own variable:
 *     `window.location.replace(\`https://microapps.${base}?userToken=${token}\`)`.
 *     Those users have no other route in — the SPA sends them straight here on every
 *     sign-in — so until the SPA ships the rename they arrive with a credential this
 *     host used to ignore entirely, and got "Secure Access Required" holding a
 *     perfectly valid token.
 *
 * Accepting both is the only fix that does not require the two apps to deploy in
 * lockstep. Once the SPA is corrected, `userToken` becomes dead weight that costs one
 * extra `searchParams.get` per page request, so it can be dropped — but not before,
 * and not without checking what else redirects here.
 *
 * This module deliberately has NO imports: middleware.ts runs in the Edge runtime and
 * cannot pull in anything that reaches for Node crypto (which is why it cannot import
 * from utils/authToken.ts, and why the list lives here rather than there).
 */

/** Param names that may carry the handover JWT, in precedence order. */
export const TOKEN_PARAMS = ['authToken', 'userToken'] as const;

/**
 * Everything to strip from the URL once the handover has been captured. `tenant` is
 * not a credential but is part of the same short-lived bootstrap pair, and `node` is
 * deliberately NOT here — it is a durable environment hint the shell reads on later
 * loads (resolveSignInUrl falls back to it), not something to clean away.
 */
export const HANDOVER_PARAMS = [...TOKEN_PARAMS, 'tenant'] as const;

/** The handover JWT from a query string, whichever name it arrived under. */
export function readHandoverToken(params: URLSearchParams): string | null {
  for (const name of TOKEN_PARAMS) {
    const value = params.get(name);
    if (value) return value;
  }
  return null;
}

/**
 * A JWT's payload, without any signature check.
 *
 * Lives here because both the Edge middleware and the browser need it and neither can
 * share the Node-only `jsonwebtoken` decode: the Edge runtime has no Node crypto. The
 * result is only ever used to read a cookie lifetime and a tenant NAME — never to
 * authorise anything — and every consumer verifies the signature itself, so a forged
 * token buys nothing beyond values those checks reject.
 */
export function decodeJwtPayload(
  token: string,
): { exp?: number; data?: { tenant?: string } } | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;

    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Is this a single DNS label — the shape a tenant/node value must have?
 *
 * It has to be checked before the value is interpolated into a hostname, because it
 * arrives from places an attacker can write: the `?node=` query param on a link they can
 * send, and the localStorage copy made from it. `https://${node}.siya.com` with node set
 * to `evil.com/#` builds `https://evil.com/#.siya.com`, which the browser resolves to
 * evil.com — and the link it lands on is the SIGN-IN button, so the destination is a page
 * asking for SIYA credentials. A one-line check is the whole fix.
 *
 * Deliberately no dots: every legitimate value is one label ('synergy', 'app-dev'), and
 * allowing dots is what lets a value climb out of the base domain.
 */
export function isTenantLabel(value: string | null | undefined): value is string {
  return !!value && /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(value);
}

/**
 * Whether the URL carries a handover param at all.
 *
 * Note this is `has`, not `readHandoverToken() !== null`: `?authToken=` with an empty
 * value still has to be STRIPPED even though there is nothing to store, otherwise the
 * caller leaves it in the address bar forever.
 */
export function hasHandoverToken(params: URLSearchParams): boolean {
  return TOKEN_PARAMS.some((name) => params.has(name));
}
